import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { RecommendedRiderHeight } from "../service/bike-rider-height-utils"
import {
  ServiceBikeBrakeSystem,
  ServiceBikeGearSystem,
  ServiceBikeType,
} from "../service/service-bike"
import { MarketplaceBikeSummaryDto } from "./marketplace-bike-dto"

/**
 * Curated quality tier shown in the "Skick" filter on julo.se. Derived from a
 * bike's overall condition percentage so the listing filter and the detail page
 * stay in sync. Kept here, in one place, so the thresholds are easy to tune.
 */
export enum BikeConditionTier {
  NYSKICK = "NYSKICK",
  MYCKET_BRA = "MYCKET_BRA",
  BRA = "BRA",
  ACCEPTABELT = "ACCEPTABELT",
  DALIGT = "DALIGT",
}

export const BIKE_CONDITION_TIER_LABEL_KEYS: Record<
  BikeConditionTier,
  TranslationKey
> = {
  [BikeConditionTier.NYSKICK]: "common.bikeConditionTier.asNew",
  [BikeConditionTier.MYCKET_BRA]: "common.bikeConditionTier.veryGood",
  [BikeConditionTier.BRA]: "common.bikeConditionTier.good",
  [BikeConditionTier.ACCEPTABELT]: "common.bikeConditionTier.acceptable",
  [BikeConditionTier.DALIGT]: "common.bikeConditionTier.poor",
}

/** Canonical best→worst order for rendering the condition tiers. */
export const BIKE_CONDITION_TIER_ORDER: ReadonlyArray<BikeConditionTier> = [
  BikeConditionTier.NYSKICK,
  BikeConditionTier.MYCKET_BRA,
  BikeConditionTier.BRA,
  BikeConditionTier.ACCEPTABELT,
  BikeConditionTier.DALIGT,
]

/**
 * Buckets an overall condition percentage (0–100) into a {@link BikeConditionTier}.
 * Thresholds are inclusive lower bounds and tunable in one place.
 */
export const conditionTierFromPercent = (
  overallPercent: number,
): BikeConditionTier => {
  if (overallPercent >= 90) return BikeConditionTier.NYSKICK
  if (overallPercent >= 75) return BikeConditionTier.MYCKET_BRA
  if (overallPercent >= 55) return BikeConditionTier.BRA
  if (overallPercent >= 35) return BikeConditionTier.ACCEPTABELT
  return BikeConditionTier.DALIGT
}

/** Suspension category shown in the "Dämpare" filter. */
export enum BikeSuspension {
  HARDTAIL = "HARDTAIL",
  FULL = "FULL",
}

export const BIKE_SUSPENSION_LABEL_KEYS: Record<
  BikeSuspension,
  TranslationKey
> = {
  [BikeSuspension.HARDTAIL]: "common.bikeSuspension.hardtail",
  [BikeSuspension.FULL]: "common.bikeSuspension.full",
}

export const BIKE_SUSPENSION_ORDER: ReadonlyArray<BikeSuspension> = [
  BikeSuspension.HARDTAIL,
  BikeSuspension.FULL,
]

/**
 * Derives the suspension category from the two damper flags. A bike with a rear
 * damper is full-suspension; a front-only damper is a hardtail; no damper at all
 * has no suspension category (`null`, so it never shows in the facet).
 */
export const bikeSuspensionFromDampers = (
  hasFrontDamper: boolean,
  hasRearDamper: boolean,
): BikeSuspension | null => {
  if (hasRearDamper) return BikeSuspension.FULL
  if (hasFrontDamper) return BikeSuspension.HARDTAIL
  return null
}

/** Parses a comma-separated query value into the subset of `allowed` members. */
const csvEnum = <T extends string>(allowed: ReadonlyArray<T>) => {
  const allowedSet: ReadonlySet<string> = new Set(allowed)
  return z
    .string()
    .optional()
    .transform((value): T[] =>
      value === undefined
        ? []
        : value
            .split(",")
            .map((part) => part.trim())
            .filter((part): part is T => allowedSet.has(part)),
    )
}

/** Parses a comma-separated query value into finite numbers. */
const csvNumbers = z
  .string()
  .optional()
  .transform((value): number[] =>
    value === undefined
      ? []
      : value
          .split(",")
          .map((part) => Number(part.trim()))
          .filter((part) => Number.isFinite(part)),
  )

/** Parses a comma-separated query value into trimmed, non-empty strings. */
const csvStrings = z
  .string()
  .optional()
  .transform((value): string[] =>
    value === undefined
      ? []
      : value
          .split(",")
          .map((part) => part.trim())
          .filter((part) => part.length > 0),
  )

/**
 * Treats an empty query value (e.g. `?priceMin=` from a cleared input) as
 * absent instead of letting `z.coerce.number()` turn `""` into `0`, which would
 * be read as an active bound. Mirrors the empty-dropping in csvNumbers/csvStrings.
 */
const emptyToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((value) => (value === "" ? undefined : value), schema)

/**
 * Query filters for the `/bikes` listing. All optional; multi-selects accept a
 * comma-separated query string and resolve to an array (empty = no constraint).
 * Sort defaults to newest.
 */
export const MarketplaceBikeFilterSchema = z.object({
  bikeType: csvEnum(Object.values(ServiceBikeType)),
  brand: csvStrings,
  color: csvStrings,
  storeName: csvStrings,
  wheelSizeInches: csvNumbers,
  gearSystem: csvEnum(Object.values(ServiceBikeGearSystem)),
  brakeSystem: csvEnum(Object.values(ServiceBikeBrakeSystem)),
  conditionTier: csvEnum(Object.values(BikeConditionTier)),
  suspension: csvEnum(Object.values(BikeSuspension)),
  /** Only when explicitly "true" — the Eldriven checkbox is a one-way filter. */
  isElectric: z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional(),
  priceMin: emptyToUndefined(z.coerce.number().nonnegative().optional()),
  priceMax: emptyToUndefined(z.coerce.number().nonnegative().optional()),
  weightMin: emptyToUndefined(z.coerce.number().nonnegative().optional()),
  weightMax: emptyToUndefined(z.coerce.number().nonnegative().optional()),
  /** Keep only bikes whose recommended rider-height range includes this height. */
  riderHeightCm: z.coerce.number().int().positive().optional(),
  sort: z.enum(["newest", "price_asc", "price_desc"]).default("newest"),
})

export type MarketplaceBikeFilter = z.infer<typeof MarketplaceBikeFilterSchema>

/** The minimal per-bike shape needed to filter and build facet counts. */
export interface BikeFilterAttrs {
  bikeType: ServiceBikeType
  isElectric: boolean
  brand: string
  color: string | null
  storeName: string
  wheelSizeInches: number | null
  gearSystem: ServiceBikeGearSystem | null
  /** Distinct front/back brake systems present on the bike, excluding NONE. */
  brakeSystems: ServiceBikeBrakeSystem[]
  conditionTier: BikeConditionTier | null
  suspension: BikeSuspension | null
  listingPrice: number | null
  weightKg: number | null
  riderHeight: RecommendedRiderHeight | null
}

/** A single checkbox-facet option with its match count given other filters. */
export interface FacetCount {
  value: string
  count: number
}

/** Min/max bounds for a range facet (price, weight) over the other-filtered set. */
export interface RangeFacet {
  min: number
  max: number
}

export interface MarketplaceBikeFacets {
  bikeType: FacetCount[]
  brand: FacetCount[]
  color: FacetCount[]
  storeName: FacetCount[]
  wheelSizeInches: FacetCount[]
  gearSystem: FacetCount[]
  brakeSystem: FacetCount[]
  conditionTier: FacetCount[]
  suspension: FacetCount[]
  /** Number of electric bikes given all other active filters. */
  isElectric: number
  price: RangeFacet | null
  weight: RangeFacet | null
}

export interface MarketplaceBikeListResponse {
  bikes: MarketplaceBikeSummaryDto[]
  facets: MarketplaceBikeFacets
}

/** The filter dimensions a facet can exclude when counting its own options. */
type FacetDimension =
  | "bikeType"
  | "brand"
  | "color"
  | "storeName"
  | "wheelSizeInches"
  | "gearSystem"
  | "brakeSystem"
  | "conditionTier"
  | "suspension"
  | "isElectric"
  | "price"
  | "weight"

/**
 * Whether a bike matches the filter. `except` skips one dimension's constraint,
 * which is how each facet counts its own options (all other filters applied).
 */
export const matchesFilter = (
  attrs: BikeFilterAttrs,
  filter: MarketplaceBikeFilter,
  except?: FacetDimension,
): boolean => {
  if (
    except !== "bikeType" &&
    filter.bikeType.length > 0 &&
    !filter.bikeType.includes(attrs.bikeType)
  ) {
    return false
  }
  if (
    except !== "brand" &&
    filter.brand.length > 0 &&
    !filter.brand.some(
      (brand) => brand.toLowerCase() === attrs.brand.toLowerCase(),
    )
  ) {
    return false
  }
  if (except !== "color" && filter.color.length > 0) {
    if (attrs.color === null) return false
    const color = attrs.color.toLowerCase()
    if (!filter.color.some((value) => value.toLowerCase() === color)) {
      return false
    }
  }
  if (
    except !== "storeName" &&
    filter.storeName.length > 0 &&
    !filter.storeName.includes(attrs.storeName)
  ) {
    return false
  }
  if (except !== "wheelSizeInches" && filter.wheelSizeInches.length > 0) {
    if (attrs.wheelSizeInches === null) return false
    if (!filter.wheelSizeInches.includes(attrs.wheelSizeInches)) return false
  }
  if (except !== "gearSystem" && filter.gearSystem.length > 0) {
    if (attrs.gearSystem === null) return false
    if (!filter.gearSystem.includes(attrs.gearSystem)) return false
  }
  if (except !== "brakeSystem" && filter.brakeSystem.length > 0) {
    if (
      !filter.brakeSystem.some((value) => attrs.brakeSystems.includes(value))
    ) {
      return false
    }
  }
  if (except !== "conditionTier" && filter.conditionTier.length > 0) {
    if (attrs.conditionTier === null) return false
    if (!filter.conditionTier.includes(attrs.conditionTier)) return false
  }
  if (except !== "suspension" && filter.suspension.length > 0) {
    if (attrs.suspension === null) return false
    if (!filter.suspension.includes(attrs.suspension)) return false
  }
  if (
    except !== "isElectric" &&
    filter.isElectric === true &&
    !attrs.isElectric
  ) {
    return false
  }
  if (except !== "price") {
    if (filter.priceMin !== undefined) {
      if (attrs.listingPrice === null || attrs.listingPrice < filter.priceMin) {
        return false
      }
    }
    if (filter.priceMax !== undefined) {
      if (attrs.listingPrice === null || attrs.listingPrice > filter.priceMax) {
        return false
      }
    }
  }
  if (except !== "weight") {
    if (filter.weightMin !== undefined) {
      if (attrs.weightKg === null || attrs.weightKg < filter.weightMin) {
        return false
      }
    }
    if (filter.weightMax !== undefined) {
      if (attrs.weightKg === null || attrs.weightKg > filter.weightMax) {
        return false
      }
    }
  }
  if (filter.riderHeightCm !== undefined) {
    if (attrs.riderHeight === null) return false
    if (
      filter.riderHeightCm < attrs.riderHeight.min ||
      filter.riderHeightCm > attrs.riderHeight.max
    ) {
      return false
    }
  }
  return true
}

/**
 * Tallies counts for one checkbox facet. Seeds every value present in the full
 * dataset at 0 so options that exist but match nothing under the current other
 * filters still appear (greyed out), then counts over the other-filtered subset.
 * Sorted by count desc, then value asc.
 */
const facetCounts = (
  all: BikeFilterAttrs[],
  filter: MarketplaceBikeFilter,
  dimension: FacetDimension,
  getValues: (attrs: BikeFilterAttrs) => string[],
): FacetCount[] => {
  const counts = new Map<string, number>()
  for (const attrs of all) {
    for (const value of getValues(attrs)) {
      if (!counts.has(value)) counts.set(value, 0)
    }
  }
  for (const attrs of all) {
    if (!matchesFilter(attrs, filter, dimension)) continue
    for (const value of getValues(attrs)) {
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
}

const rangeFacet = (
  all: BikeFilterAttrs[],
  filter: MarketplaceBikeFilter,
  dimension: "price" | "weight",
  getValue: (attrs: BikeFilterAttrs) => number | null,
): RangeFacet | null => {
  const values: number[] = []
  for (const attrs of all) {
    if (!matchesFilter(attrs, filter, dimension)) continue
    const value = getValue(attrs)
    if (value !== null) values.push(value)
  }
  if (values.length === 0) return null
  return { min: Math.min(...values), max: Math.max(...values) }
}

/** Computes all facet counts for the listing sidebar. */
export const computeFacets = (
  all: BikeFilterAttrs[],
  filter: MarketplaceBikeFilter,
): MarketplaceBikeFacets => ({
  bikeType: facetCounts(all, filter, "bikeType", (a) => [a.bikeType]),
  brand: facetCounts(all, filter, "brand", (a) => [a.brand]),
  color: facetCounts(all, filter, "color", (a) => (a.color ? [a.color] : [])),
  storeName: facetCounts(all, filter, "storeName", (a) => [a.storeName]),
  wheelSizeInches: facetCounts(all, filter, "wheelSizeInches", (a) =>
    a.wheelSizeInches === null ? [] : [String(a.wheelSizeInches)],
  ),
  gearSystem: facetCounts(all, filter, "gearSystem", (a) =>
    a.gearSystem === null ? [] : [a.gearSystem],
  ),
  brakeSystem: facetCounts(all, filter, "brakeSystem", (a) => a.brakeSystems),
  conditionTier: facetCounts(all, filter, "conditionTier", (a) =>
    a.conditionTier === null ? [] : [a.conditionTier],
  ),
  suspension: facetCounts(all, filter, "suspension", (a) =>
    a.suspension === null ? [] : [a.suspension],
  ),
  isElectric: all.filter(
    (a) => matchesFilter(a, filter, "isElectric") && a.isElectric,
  ).length,
  price: rangeFacet(all, filter, "price", (a) => a.listingPrice),
  weight: rangeFacet(all, filter, "weight", (a) => a.weightKg),
})

/**
 * Distinct, NONE-excluded brake systems for a bike, from its front/back systems.
 * Shared by the service when building {@link BikeFilterAttrs}.
 */
export const bikeBrakeSystems = (
  front: ServiceBikeBrakeSystem | null,
  back: ServiceBikeBrakeSystem | null,
): ServiceBikeBrakeSystem[] => {
  const systems = new Set<ServiceBikeBrakeSystem>()
  if (front !== null && front !== ServiceBikeBrakeSystem.NONE)
    systems.add(front)
  if (back !== null && back !== ServiceBikeBrakeSystem.NONE) systems.add(back)
  return [...systems]
}
